import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supervisorApi } from '@/features/workspace/api';
import { useToast } from '@/hooks/useToast';
import { routes } from '@/lib/routes';
import '../b4-moderation.css';

type ModerationRow = {
  id: string;
  request_code: string;
  student_name: string;
  service_name: string;
  topic_link: string;
  status: string;
  decision_note: string | null;
};

const TABS = [
  { id: 'pending', label: 'Ожидают' },
  { id: 'approved', label: 'Разрешены' },
  { id: 'rejected', label: 'Отклонены' },
] as const;

export const ModerationPanel = ({ onClose }: { onClose?: () => void }) => {
  const navigate = useNavigate();
  const { notify } = useToast();
  const close = onClose ?? (() => navigate(routes.supervisor));
  const [tab, setTab] = useState<(typeof TABS)[number]['id']>('pending');
  const [items, setItems] = useState<ModerationRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [note, setNote] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const payload = await supervisorApi.module('moderation', { tab });
      setItems((payload.items as ModerationRow[] | undefined) ?? []);
    } catch {
      notify('Не удалось загрузить модерацию', 'error');
    } finally {
      setLoading(false);
    }
  }, [notify, tab]);

  useEffect(() => {
    void load();
  }, [load]);

  const decide = async (id: string, decision: 'APPROVED' | 'REJECTED') => {
    setBusyId(id);
    try {
      await supervisorApi.decideModeration(id, { decision, note: note.trim() || undefined });
      notify(decision === 'APPROVED' ? 'Заявка разрешена' : 'Заявка отклонена', 'success');
      setNote('');
      await load();
    } catch {
      notify('Не удалось зафиксировать решение', 'error');
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="b4-root" role="dialog" aria-modal="true" aria-label="Модерация заявок обучающихся">
      <header className="b4-top">
        <button type="button" onClick={close}>← Назад</button>
        <div>
          <h1>Модерация заявок обучающихся</h1>
          <p>SUPERVISOR · бесплатное использование услуг</p>
        </div>
        <button type="button" onClick={close}>Закрыть ×</button>
      </header>
      <main className="b4-content">
        <div className="b4-tabs">
          {TABS.map((item) => (
            <button key={item.id} type="button" className={tab === item.id ? 'active' : ''} onClick={() => setTab(item.id)}>
              {item.label}
            </button>
          ))}
        </div>
        <label className="b4-field">
          <span>Комментарий к решению</span>
          <input value={note} onChange={(event) => setNote(event.target.value)} placeholder="Причина / уточнение для обучающегося" />
        </label>
        <section className="b4-panel">
          <div className="b4-moderation-list">
            {loading ? <p>Загрузка…</p> : null}
            {!loading && items.length === 0 ? <p>Нет записей в этой выборке</p> : null}
            {items.map((item) => (
              <article className="b4-moderation-item" key={item.id}>
                <div className="b4-kicker">{item.request_code}</div>
                <h3>{item.student_name}</h3>
                <p>
                  <b>Услуга:</b> {item.service_name}<br />
                  <b>Тема:</b> {item.topic_link}<br />
                  <b>Связь:</b> Связана с утверждённой учебной задачей / темой
                </p>
                <span className={`b4-chip ${item.status === 'APPROVED' ? 'ok' : item.status === 'REJECTED' ? 'bad' : 'warn'}`}>{item.status}</span>
                {item.decision_note ? <small>{item.decision_note}</small> : null}
                {item.status === 'PENDING_SUPERVISOR' ? (
                  <div className="b4-actions">
                    <button type="button" className="b4-btn mint" disabled={busyId === item.id} onClick={() => void decide(item.id, 'APPROVED')}>Разрешить</button>
                    <button type="button" className="b4-btn red" disabled={busyId === item.id} onClick={() => void decide(item.id, 'REJECTED')}>Отклонить</button>
                  </div>
                ) : null}
              </article>
            ))}
          </div>
        </section>
      </main>
    </div>
  );
};
